import { useState } from "react";
import { toast } from "sonner";
import { Lock, Check, Loader2, Play } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { stickerUrl, playSfx } from "@/components/editor/stickers/fx";
import { useHoliday, useOwnedPacks } from "@/components/holiday/useHoliday";

const PackCard = ({ p, own, busy, disabled, onBuy }) => {
  const { t, lang } = useI18n();
  return (
    <div className="cx-plan-frame rounded-2xl p-[1.5px]" style={{ "--c": p.color }} data-testid={`holiday-pack-${p.id}`}>
      <div className="rounded-2xl bg-[#0c0d1c] p-5 h-full flex flex-col">
        <div className="flex items-center justify-between gap-2">
          <span className="font-display text-lg font-bold" style={{ color: p.color }}>{lang === "fr" ? p.fr : p.en}</span>
          <span className="text-[10px] font-mono text-slate-400">{t("hol.pack_count", { s: p.stickers.length, n: p.sfx.length })}</span>
        </div>
        <div className="grid grid-cols-4 gap-2 mt-4">
          {p.stickers.map((s) => <img key={s.id} src={stickerUrl(s.id)} alt={s.text} className="w-full h-14 object-contain drop-shadow-[0_4px_8px_rgba(0,0,0,.6)] transition-transform duration-200 hover:scale-110" />)}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {p.sfx.map((s) => <button key={s.id} type="button" onClick={() => playSfx(s.id)} data-testid={`holiday-sfx-${s.id}`}
            className="flex items-center gap-1 rounded-full border border-white/10 px-2.5 py-1 text-[10px] text-slate-300 hover:border-white/30"><Play className="w-2.5 h-2.5 fill-current" />{lang === "fr" ? s.fr : s.en}</button>)}
        </div>
        <div className="mt-auto pt-5">
          {own
            ? <div className="flex items-center justify-center gap-1.5 rounded-full py-2.5 text-sm font-semibold text-[#22e3a1] border border-[#22e3a1]/40 bg-[#22e3a1]/10" data-testid={`holiday-owned-${p.id}`}><Check className="w-4 h-4" />{t("hol.owned")}</div>
            : <button type="button" disabled={disabled} onClick={() => onBuy(p)} data-testid={`holiday-buy-${p.id}`}
                className="w-full cx-btn rounded-full py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}{t("hol.buy", { n: p.price })}
              </button>}
        </div>
      </div>
    </div>
  );
};

export const HolidayPacks = ({ demo, onBought }) => {
  const { t } = useI18n();
  const { packs } = useHoliday();
  const [owned, , setOwned] = useOwnedPacks();
  const [busy, setBusy] = useState(null);
  if (!packs.length) return null;
  const buy = async (p) => {
    setBusy(p.id);
    try {
      const r = await api.post(`/holiday/packs/${p.id}/buy`);
      setOwned(r.data.owned);
      toast.success(t("hol.bought"));
      onBought?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };
  return (
    <section id="holiday-packs" className="mt-12 scroll-mt-24" data-testid="holiday-packs">
      <h2 className="text-base md:text-lg font-semibold text-white">{t("hol.packs_title")}</h2>
      <p className="text-sm text-slate-400 mt-1">{t("hol.packs_sub")}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mt-5">
        {packs.map((p) => <PackCard key={p.id} p={p} own={owned.includes(p.id)} busy={busy === p.id} disabled={demo || !!busy} onBuy={buy} />)}
      </div>
    </section>
  );
};
